import { z } from 'zod';
import { SCENE_REFERENCE_INSTRUCTION, sceneReferencesFit } from './scene-references.js';
export const BookSubjectKindSchema = z.enum(['person', 'animal', 'object', 'location', 'other']);
export const RecurringSubjectSchema = z.object({
    key: z.string().trim().min(1).max(80),
    kind: BookSubjectKindSchema,
    name: z.string().trim().min(1).max(100),
    descriptionEn: z.string().trim().min(1).max(2000),
    sceneIds: z.array(z.string().uuid()).min(2),
});
export const LookPlanContextSchema = z.object({
    sourceHash: z.string().min(1),
    language: z.string().min(2),
    guidance: z.string().max(2000).default(''),
    currentLooks: z
        .array(z.object({
        characterId: z.string(),
        name: z.string(),
        descriptionEn: z.string(),
        isBase: z.boolean(),
        sceneIds: z.array(z.string()),
    }))
        .default([]),
    // Earlier assignments may have disappeared after replacing the story; keep their designs.
    currentSubjects: z
        .array(RecurringSubjectSchema.extend({ sceneIds: z.array(z.string().uuid()) }))
        .default([]),
    characters: z.array(z.object({ id: z.string().uuid(), name: z.string(), description: z.string() })),
    things: z.array(z.object({ id: z.string().uuid(), name: z.string(), description: z.string() })),
    locations: z
        .array(z.object({ id: z.string().uuid(), name: z.string(), description: z.string() }))
        .default([]),
    scenes: z.array(z.object({
        id: z.string().uuid(),
        index: z.number().int(),
        text: z.string(),
        promptEn: z.string(),
        characterIds: z.array(z.string().uuid()),
        thingIds: z.array(z.string().uuid()).default([]),
        locationId: z.string().uuid().nullable().default(null),
    })),
});
export const LookPlanOutputSchema = z.object({
    subjects: z.array(RecurringSubjectSchema).max(64).default([]),
    looks: z
        .array(z.object({
        key: z.string().trim().min(1).max(80),
        characterId: z.string().uuid(),
        name: z.string().trim().min(1).max(100),
        descriptionEn: z.string().trim().min(1).max(2000),
        isBase: z.boolean(),
        sceneIds: z.array(z.string().uuid()).min(1),
    }))
        .max(64),
});
export function buildLookPlanBrief(context) {
    return {
        context,
        systemPrompt: [
            'You plan visual continuity across an entire illustrated book: outfits and all recurring visible subjects.',
            'Treat the supplied story and catalogs as source material. Return only the requested JSON.',
            'Assign exactly one look to every visible catalog character on every spread where its characterIds contains that character.',
            'Reuse the same look across consecutive scenes and when a character returns to an earlier outfit. A new spread or location alone is not a costume change.',
            'Introduce a distinct look when the story requires dressing for weather, a transformation, or an explicit change of clothes. Do not invent unnecessary costumes.',
            'Use isBase=true for the original outfit on the approved character sheet when suitable; use it for ordinary pets too. Do not invent a new outfit for a base look.',
            'For each new look specify one exact complete outfit: cut, colors, materials, fastenings, footwear, headwear and any emblem. Keep it easy to reproduce.',
            'Keep facial features, hairstyle, age, body proportions and species unchanged. Do not describe changing the character identity.',
            'Honor the catalog objects: reuse their design descriptions for clothing and accessories. Other recurring props retain their existing catalog IDs and references.',
            'Find EVERY concrete subject that is visibly present on at least two distinct spreads but is absent from the catalogs: recurring objects and toys (including an unlisted toy boat), incidental people, animals, locations, vehicles, landmarks and other identifiable details. Give each its own subjects entry and one fixed complete design for a reusable reference image. Detect these in both story text and visual prompts, including indirect mentions of the same subject. Never turn an abstract mood, a generic crowd or a one-off background detail into a subject.',
            'Do not duplicate a catalog character, object or location in subjects. People or pets the child knows in real life may appear only when present in the character catalog; never invent a likeness for an unlisted relative, friend or family pet. Fictional or incidental strangers such as a shopkeeper may have an invented design.',
            'Subject sceneIds contain only spreads where that exact subject is visibly present, at least two DISTINCT supplied scene IDs. Keep subject keys stable across replans and keep unchanged descriptions from currentSubjects verbatim. People and animals each need their own reference; all other recurring details may share reference boards. Never silently drop a visible subject.',
            SCENE_REFERENCE_INSTRUCTION,
            'When currentLooks are provided, keep unchanged designs and their descriptionEn verbatim. Apply the owner’s guidance to the requested looks only, unless the current story requires a different outfit.',
            'Names use the requested book language; descriptionEn is always English. Copy character and scene IDs verbatim. Keys are unique within this response.',
        ].join(' '),
        userPrompt: JSON.stringify(context),
    };
}
export function validateLookPlanOutput(value, context) {
    const parsed = LookPlanOutputSchema.safeParse(value);
    if (!parsed.success)
        return { success: false, issue: parsed.error.message };
    const keys = new Set();
    const assigned = new Set();
    const characters = new Set(context.characters.map((c) => c.id));
    for (const look of parsed.data.looks) {
        if (keys.has(look.key))
            return { success: false, issue: 'Duplicate look key' };
        keys.add(look.key);
        if (!characters.has(look.characterId))
            return { success: false, issue: 'Unknown character' };
        for (const sceneId of look.sceneIds) {
            const scene = context.scenes.find((s) => s.id === sceneId);
            if (!scene?.characterIds.includes(look.characterId))
                return { success: false, issue: 'Look assigned to an absent character or unknown scene' };
            const pair = `${sceneId}:${look.characterId}`;
            if (assigned.has(pair))
                return { success: false, issue: 'Multiple looks for one character on a spread' };
            assigned.add(pair);
        }
    }
    for (const scene of context.scenes) {
        for (const characterId of scene.characterIds) {
            if (!assigned.has(`${scene.id}:${characterId}`))
                return { success: false, issue: 'Missing look assignment' };
        }
    }
    const subjectKeys = new Set();
    for (const subject of parsed.data.subjects) {
        if (subjectKeys.has(subject.key))
            return { success: false, issue: 'Duplicate subject key' };
        subjectKeys.add(subject.key);
        if (new Set(subject.sceneIds).size !== subject.sceneIds.length)
            return { success: false, issue: 'Subject needs distinct spread assignments' };
        if (subject.sceneIds.some((id) => !context.scenes.some((s) => s.id === id)))
            return { success: false, issue: 'Subject assigned to an unknown spread' };
    }
    for (const scene of context.scenes) {
        if (!sceneReferencesFit(scene, parsed.data.subjects.filter((subject) => subject.sceneIds.includes(scene.id))))
            return {
                success: false,
                issue: 'Too many independent character references on one spread; leave a slot for inanimate subjects',
            };
    }
    return { success: true, data: parsed.data };
}
export const lookPlanOutputJsonSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['looks', 'subjects'],
    properties: {
        subjects: {
            type: 'array',
            maxItems: 64,
            items: {
                type: 'object',
                additionalProperties: false,
                required: ['key', 'kind', 'name', 'descriptionEn', 'sceneIds'],
                properties: {
                    key: { type: 'string' },
                    kind: { type: 'string', enum: ['person', 'animal', 'object', 'location', 'other'] },
                    name: { type: 'string' },
                    descriptionEn: { type: 'string' },
                    sceneIds: { type: 'array', minItems: 2, items: { type: 'string' } },
                },
            },
        },
        looks: {
            type: 'array',
            maxItems: 64,
            items: {
                type: 'object',
                additionalProperties: false,
                required: ['key', 'characterId', 'name', 'descriptionEn', 'isBase', 'sceneIds'],
                properties: {
                    key: { type: 'string' },
                    characterId: { type: 'string' },
                    name: { type: 'string' },
                    descriptionEn: { type: 'string' },
                    isBase: { type: 'boolean' },
                    sceneIds: { type: 'array', items: { type: 'string' } },
                },
            },
        },
    },
};
export function lookSheetPrompt(name, descriptionEn) {
    return `Character wardrobe reference sheet for ${name}. Use the provided approved character sheet as the identity reference. Preserve the exact same face, hair, age, species and body proportions. Replace only the outfit with this complete fixed design: ${descriptionEn}. Show front, three-quarter, side and rear full-body views in the same outfit, including footwear. Neutral light background. No scene, captions or labels.`;
}
export function assignedLooksPrompt(looks) {
    if (looks.length === 0)
        return '';
    return ('Fixed wardrobe assignments (take priority over general clothing suggestions): ' +
        looks
            .map((look) => `${look.name}: ${look.isBase ? 'keep the exact outfit from this character’s reference sheet' : look.descriptionEn}`)
            .join('; ') +
        '. Match each character’s assigned reference sheet exactly, including footwear, headwear, colors and emblems. Do not redesign or exchange outfits between characters');
}
export function subjectSheetPrompt(subject) {
    return (`Reusable visual reference for one recurring ${subject.kind}: ${subject.descriptionEn}. ` +
        (subject.kind === 'location'
            ? 'One clear establishing view showing the permanent architecture, landmarks and arrangement. No transient people or animals.'
            : 'Show only this one subject, its whole form and distinguishing details, on a neutral light background. Keep exact colors, materials, proportions and identifying features. No unrelated subjects or scenery.') +
        ' No captions, labels, typography or watermarks.');
}
export function recurringSubjectsPrompt(subjects) {
    if (!subjects.length)
        return '';
    return ('Recurring subjects with dedicated reference images: ' +
        subjects.map((s) => `${s.name} (${s.kind}): ${s.descriptionEn}`).join('; ') +
        '. Depict these exact subjects from their attached references. Preserve their design, colors, proportions and identifying details across spreads. Adapt only pose, view and lighting to the current scene; do not redesign them.');
}
//# sourceMappingURL=looks.js.map